/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types/disaEntries.types.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    TypeScript type definitions for DISA Entries module.
    Centralized types for better type safety and maintainability.
────────────────────────────────────────────────────────────────*/

/**
 * DISA Entry interface
 */
export interface DisaEntry {
  id: number;
  uuid: string;
  domainId: number;
  name: string;
  context: string;
  password: string;
  classOfServicesId?: number;
  classOfServiceName?: string;
  dialPrefix?: string;
  callerIdName?: string;
  callerIdNumber?: string;
  responseTimeout?: number;
  digitTimeout?: number;
  settings?: DisaSettings;
  enabled: boolean;
  createdAt: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
}

/**
 * DISA Settings interface
 */
export interface DisaSettings {
  dialPrefix: string;
  callerIdName: string;
  callerIdNumber: string;
  inheritCallerId: boolean;
  greeting: string;
  invalidPinSound: string;
  timeoutSound: string;
  responseTimeout: number;
  digitTimeout: number;
  maxCallDuration: number;
  maxAttempts: number;
  hangupOnFail: boolean;
  recordCall: boolean;
  logAccess: boolean;
  allowLocal: boolean;
  allowInternational: boolean;
  ipRestriction: string[];
  scheduleWindow: string | null;
  retryOnFail: boolean;
  notifyOnAccess: string;
}

/**
 * Form state interface
 */
export interface FormState {
  name: string;
  context: string;
  password: string;
  classOfServicesId: number | null;
  enabled: boolean;
  settings: DisaSettings;
}

/**
 * Create DISA entry payload
 */
export interface CreateDisaEntryPayload {
  name: string;
  context: string;
  password: string;
  classOfServicesId: number | null;
  enabled: boolean;
  settings: DisaSettingsPayload;
}

/**
 * Update DISA entry payload
 */
export interface UpdateDisaEntryPayload {
  name?: string;
  context?: string;
  password?: string;
  classOfServicesId?: number | null;
  enabled?: boolean;
  settings?: Partial<DisaSettingsPayload>;
}

/**
 * Settings payload for API (snake_case)
 */
export interface DisaSettingsPayload {
  dial_prefix: string | null;
  caller_id_name: string | null;
  caller_id_number: string | null;
  inherit_caller_id: boolean;
  greeting: string | null;
  invalid_pin_sound: string;
  timeout_sound: string;
  response_timeout: number;
  digit_timeout: number;
  max_call_duration: number;
  max_attempts: number;
  hangup_on_fail: boolean;
  record_call: boolean;
  log_access: boolean;
  allow_local: boolean;
  allow_international: boolean;
  ip_restriction: string[];
  schedule_window: string | null;
  retry_on_fail: boolean;
  notify_on_access: string | null;
}

/**
 * Validation errors interface
 */
export interface ValidationErrors {
  [key: string]: string;
}

/**
 * List query parameters
 */
export interface ListDisaEntriesParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

/**
 * List response
 */
export interface ListDisaEntriesResponse {
  success: boolean;
  data: DisaEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Class of Service option
 */
export interface ClassOfServiceOption {
  id: number;
  name: string;
}

/**
 * Constants
 */
export const DISA_NAME_MAX = 128;
export const PASSWORD_MAX = 64;
export const CONTEXT_MAX = 50;

